<template>
  <div class="modal-overlay">
    <div class="modal">
      <div class="modal-header">
        <h2>Review Resource Submission</h2>
        <button @click="$emit('close')" class="btn-close">&times;</button>
      </div>

      <div v-if="loading" class="modal-body loading-state">Loading...</div>

      <template v-else-if="resource">
        <div class="modal-body">
          <div v-if="error" class="error-banner">{{ error }}</div>

          <!-- Resource Info -->
          <div class="resource-card">
            <div class="resource-header">
              <h3>{{ resource.title }}</h3>
              <div class="resource-meta">
                <span class="meta-item">
                  Company: <strong>{{ resource.submitted_by_company?.name || '—' }}</strong>
                </span>
                <span v-if="resource.type" class="meta-item">
                  Type: <strong>{{ resource.type }}</strong>
                </span>
                <span v-if="resource.target_audience" class="meta-item">
                  Audience: <strong>{{ resource.target_audience }}</strong>
                </span>
              </div>
            </div>

            <div v-if="resource.description" class="resource-desc">
              <label>Description</label>
              <p>{{ resource.description }}</p>
            </div>

            <div v-if="resource.url" class="resource-url">
              <label>URL</label>
              <span class="url-text">{{ resource.url }}</span>
            </div>
          </div>

          <!-- Review Notes -->
          <div class="form-group">
            <label>Review Notes (optional)</label>
            <textarea
              v-model="reviewNotes"
              class="textarea"
              rows="3"
              placeholder="Add feedback or reason for decision..."
              maxlength="2000"
            />
          </div>

          <!-- Decision info -->
          <div class="decision-info">
            <div class="decision-item approve">
              <strong>Approve</strong>: The resource will become a global resource, visible to all companies.
            </div>
            <div class="decision-item reject">
              <strong>Reject</strong>: The company will be notified and can revise their submission.
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="$emit('close')" class="btn-cancel">Cancel</button>
          <button
            @click="reject"
            :disabled="actionLoading"
            class="btn-reject"
          >{{ actionLoading === 'reject' ? 'Rejecting...' : 'Reject' }}</button>
          <button
            @click="approve"
            :disabled="actionLoading"
            class="btn-approve"
          >{{ actionLoading === 'approve' ? 'Approving...' : 'Approve' }}</button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import resourceReviewService from '@/services/resourceReviewService'

const props = defineProps({ resourceId: { type: Number, required: true } })
const emit = defineEmits(['close', 'reviewed'])

const resource = ref(null)
const loading = ref(true)
const actionLoading = ref(null)
const error = ref('')
const reviewNotes = ref('')

const loadResource = async () => {
  loading.value = true
  try {
    // The submissions list already has the data; we load from the list endpoint filtered by id
    // We re-use the submissions endpoint and find the item, or just store it from the parent.
    // For simplicity, we pass resource data via a separate approach.
    // The backend doesn't have a single resource endpoint in the review controller,
    // so we load from the submissions list and find the matching one.
    const res = await resourceReviewService.getSubmissions({ page: 1, per_page: 100 })
    const items = res.data?.data || []
    resource.value = items.find(r => r.id === props.resourceId) || null
    if (!resource.value) {
      error.value = 'Resource not found in pending submissions.'
    }
  } catch {
    error.value = 'Failed to load resource.'
  } finally {
    loading.value = false
  }
}

const approve = async () => {
  if (!confirm('Approve this resource? It will become a global resource visible to all companies.')) return
  actionLoading.value = 'approve'
  error.value = ''
  try {
    await resourceReviewService.approveResource(props.resourceId, { review_notes: reviewNotes.value || undefined })
    emit('reviewed')
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to approve resource.'
    actionLoading.value = null
  }
}

const reject = async () => {
  if (!confirm('Reject this submission?')) return
  actionLoading.value = 'reject'
  error.value = ''
  try {
    await resourceReviewService.rejectResource(props.resourceId, { review_notes: reviewNotes.value || undefined })
    emit('reviewed')
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to reject resource.'
    actionLoading.value = null
  }
}

onMounted(loadResource)
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal {
  background: white;
  border-radius: 0.75rem;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.modal-header h2 {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  line-height: 1;
  padding: 0 0.25rem;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.loading-state {
  color: #6b7280;
  text-align: center;
  padding: 3rem;
}

.error-banner {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.resource-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.resource-header h3 {
  margin: 0 0 0.5rem;
  font-size: 1rem;
  font-weight: 700;
  color: #1f2937;
}

.resource-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.meta-item {
  font-size: 0.875rem;
  color: #6b7280;
}

.resource-desc label, .resource-url label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
  display: block;
  margin-bottom: 0.25rem;
}

.resource-desc p {
  margin: 0;
  font-size: 0.875rem;
  color: #4b5563;
  line-height: 1.5;
}

.url-text {
  font-size: 0.875rem;
  color: #3b82f6;
  word-break: break-all;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.textarea {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #1f2937;
  resize: vertical;
  font-family: inherit;
}

.textarea:focus { outline: none; border-color: #3b82f6; }

.decision-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.decision-item {
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.85rem;
}

.decision-item.approve { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; }
.decision-item.reject { background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; }

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.btn-cancel {
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
}

.btn-cancel:hover { background: #f9fafb; }

.btn-reject {
  padding: 0.5rem 1.25rem;
  background: white;
  color: #dc2626;
  border: 1px solid #dc2626;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
}

.btn-reject:hover:not(:disabled) { background: #fef2f2; }
.btn-reject:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-approve {
  padding: 0.5rem 1.25rem;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
}

.btn-approve:hover:not(:disabled) { background: #059669; }
.btn-approve:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
